import React, { useMemo } from "react";
import CoverPhotos from "@/components/CoverPhotos";
import SearchWidget from "@/components/SearchWidget";
import ServiceIntro from "@/components/ServiceIntro";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { coverRegionOf, homeCoversFor } from "@/data/homeCovers";
import useVisitorContinent from "@/hooks/useVisitorContinent";
import { pick } from "@/lib/languages";
import { hotelsL } from "@/i18n/stays";
import { wolofGreeting } from "@/i18n/wolof";

// The greeting of a signed-in visitor, by first name.
const GREETING = {
  en: (name) => `Where to next, ${name}?`,
  fr: (name) => `Où partez-vous, ${name} ?`,
  es: (name) => `¿A dónde vas, ${name}?`,
  pt: (name) => `Para onde vai, ${name}?`,
  ar: (name) => `إلى أين تسافر يا ${name}؟`,
  zh: (name) => `${name}，下一站去哪里？`,
  wo: wolofGreeting,
};

/**
 * The route arc: the logo's curved arrow drawn as a journey, from an open
 * origin to the red dot. It fills the right half of the band on a wide
 * screen while the visitor's continent has no cover photographs yet (they
 * take its place). Decorative and mirrored in right-to-left pages; nothing
 * on a phone.
 */
function HeroRouteArc() {
  return (
    <svg viewBox="0 0 440 210" width="440" height="210" preserveAspectRatio="xMaxYMid meet" aria-hidden="true" focusable="false" data-testid="hero-route-arc"
      className="landing-hero-arc rtl:-scale-x-100">
      <path d="M14 196C110 30 300 10 420 96" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="1 10" className="text-touki-muted" />
      <path d="M60 204C170 90 300 80 392 150" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-touki-ink-soft" />
      <circle cx="14" cy="196" r="6" strokeWidth="2" className="fill-touki-black stroke-white/70" />
      <circle cx="420" cy="96" r="9" className="fill-touki-red" />
    </svg>
  );
}

/**
 * Home's opening. Home is the stays page: the stays introduction (or a
 * signed-in visitor's greeting) on the night-blue band, over the cover
 * photographs of the visitor's continent (data/homeCovers.js; the route arc
 * while there are none), and one white card straddling the band's edge with
 * the stays search. The products are the header's row, as on every page
 * (owner, 2026-10-04): from Séjours to Vols the row stays where it is, only
 * the current tab changes.
 */
export default function HomeHero() {
  const { user } = useAuth();
  const { lang, siteLang = lang, t } = useSettings();
  const visitor = useVisitorContinent();
  const region = coverRegionOf(visitor);
  // Nothing until the visitor's country is known, so a European never sees
  // another region's photograph flash by first.
  const covers = useMemo(() => (visitor.loading ? [] : homeCoversFor(region)), [visitor.loading, region]);
  const firstName = user?.name?.trim()?.split(/\s+/)[0] || null;
  // The presentation follows the visitor's language, Wolof included.
  const stays = pick(hotelsL, siteLang);
  const title = firstName ? pick(GREETING, siteLang)(firstName) : stays.headline || t("hero.title");

  return (
    <section className="landing-hero--home relative" data-testid="home-hero">
      <div className="landing-hero-band tk-on-ink bg-touki-dark-surface text-white relative overflow-hidden">
        <CoverPhotos key={region || "world"} covers={covers} base="/covers/home" testId="home-covers" />
        <div className="relative z-raised mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <ServiceIntro service="stays" label={t("nav.stays")} title={title} description={stays.sub || t("hero.sub")}
            accent={firstName ? undefined : stays.heroAccent} tone="dark" titleTestId="hero-title" />
          {!covers.length && <HeroRouteArc />}
        </div>
      </div>
      <div className="landing-hero-card relative z-raised mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
        <div className="landing-hero-panel">
          <SearchWidget defaultTab="hotels" />
        </div>
      </div>
    </section>
  );
}
